
// src/hooks/useAuth.js
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authAPI } from '../api/auth';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';

// Query keys
export const authKeys = {
  all: ['auth'],
  profile: ['profile'],
  user: (id) => ['user', id],
};

// Helper function to clear all auth data
const clearAuthData = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userData');
  localStorage.removeItem('isProfileCreated');
  localStorage.removeItem('tempUserId');
  localStorage.removeItem('token');
  
  // Dispatch events to update UI
  window.dispatchEvent(new Event('userUpdated'));
  window.dispatchEvent(new Event('authStateChanged'));
};

// Hook for sending OTP
export const useSendOTP = () => {
  return useMutation({
    mutationFn: authAPI.sendOTP,
    onSuccess: (data) => {
      localStorage.setItem('tempUserId', data.userId);
    },
    onError: (error) => {
      console.error('Send OTP error:', error);
    },
  });
};

// Hook for verifying OTP
export const useVerifyOTP = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ userId, otp }) => authAPI.verifyOTP(userId, otp),
    onSuccess: async (data) => {
      // Store auth data
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('userId', data.userId);
      localStorage.removeItem('tempUserId');

      // Fetch profile immediately
      try {
        const profileData = await authAPI.getProfile();
        const userData = profileData.user || profileData;

        if (userData.firstName && userData.lastName) {
          // Profile complete — go to dashboard
          localStorage.setItem('isProfileCreated', 'true');
          localStorage.setItem('userData', JSON.stringify(userData));
          queryClient.setQueryData(authKeys.profile, profileData);
          window.dispatchEvent(new Event('userUpdated'));
          window.dispatchEvent(new Event('authStateChanged'));
          navigate('/dashboard');
        } else {
          // Profile incomplete — fire event so Header opens Register modal
          localStorage.setItem('isProfileCreated', 'false');
          queryClient.setQueryData(authKeys.profile, profileData);
          window.dispatchEvent(new Event('userUpdated'));
          window.dispatchEvent(new Event('authStateChanged'));
          // Fire event to open register modal
          window.dispatchEvent(new Event('openRegisterModal'));
        }
      } catch (error) {
        console.error('Profile fetch error:', error);
        localStorage.setItem('isProfileCreated', 'false');
        window.dispatchEvent(new Event('userUpdated'));
        window.dispatchEvent(new Event('authStateChanged'));
        // Fire event to open register modal
        window.dispatchEvent(new Event('openRegisterModal'));
      }
    },
    onError: (error) => {
      console.error('Verify OTP error:', error);
    },
  });
};

// Hook for fetching user profile
export const useUserProfile = () => {
  return useQuery({
    queryKey: authKeys.profile,
    queryFn: authAPI.getProfile,
    enabled: !!localStorage.getItem('authToken'),
    retry: false,
    staleTime: 5 * 60 * 1000,
    onSuccess: (data) => {
      const userData = data.user || data;
      if (userData.firstName && userData.lastName) {
        localStorage.setItem('isProfileCreated', 'true');
        localStorage.setItem('userData', JSON.stringify(userData));
      } else {
        localStorage.setItem('isProfileCreated', 'false');
      }
      window.dispatchEvent(new Event('userUpdated'));
      window.dispatchEvent(new Event('authStateChanged'));
    },
    onError: (error) => {
      console.error('Profile fetch error:', error);
      if (error.response?.status === 401) {
        clearAuthData();
      }
    },
  });
};

// Hook for updating profile
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (profileData) => {
      const response = await apiClient.put('me', profileData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.profile, data);
      queryClient.invalidateQueries({ queryKey: authKeys.profile });
      
      // Update localStorage with new user data
      const userData = data.user || data;
      if (userData.firstName && userData.lastName) {
        localStorage.setItem('isProfileCreated', 'true');
        localStorage.setItem('userData', JSON.stringify(userData));
      }
      
      window.dispatchEvent(new Event('userUpdated'));
      window.dispatchEvent(new Event('authStateChanged'));
    },
    onError: (error) => {
      console.error('Update profile error:', error);
      if (error.response?.status === 401) {
        clearAuthData();
        navigate('/');
      }
    },
  });
};

// Hook for logout
export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return () => {
    clearAuthData();
    queryClient.clear();
    navigate('/');
  };
};